import { SeededRandom } from '../simulation/SeededRandom'
import type {
  ChunkArchetype,
  ConnectorProfile,
  ObstacleSlot,
  SafeCorridor,
  TrackChunkDefinition,
} from './ChunkTypes'

interface ArchetypeSpec {
  readonly id: ChunkArchetype
  readonly minimumTier: number
  readonly weight: number
}

const ARCHETYPES: readonly ArchetypeSpec[] = [
  { id: 'straight', minimumTier: 0, weight: 9 },
  { id: 'narrow', minimumTier: 1, weight: 5 },
  { id: 'slalom', minimumTier: 1, weight: 7 },
  { id: 'gap', minimumTier: 1, weight: 4 },
  { id: 'split', minimumTier: 2, weight: 4 },
  { id: 'tunnel', minimumTier: 2, weight: 4 },
  { id: 'banking', minimumTier: 2, weight: 4 },
  { id: 'ramp', minimumTier: 2, weight: 4 },
  { id: 'moving-gates', minimumTier: 2, weight: 5 },
  { id: 'rotating-hazards', minimumTier: 3, weight: 4 },
  { id: 'crusher', minimumTier: 3, weight: 3 },
  { id: 'gauntlet', minimumTier: 4, weight: 3 },
  { id: 'void-islands', minimumTier: 3, weight: 3 },
  { id: 'wave', minimumTier: 2, weight: 4 },
  { id: 'precision-corridor', minimumTier: 4, weight: 3 },
]

function difficultyTier(distance: number): number {
  if (distance < 200) return 0
  if (distance < 600) return 1
  if (distance < 1_500) return 2
  if (distance < 4_000) return 3
  return 4
}

function weightedChoice(random: SeededRandom, tier: number): ArchetypeSpec {
  const allowed = ARCHETYPES.filter((entry) => entry.minimumTier <= tier)
  const totalWeight = allowed.reduce((sum, entry) => sum + entry.weight, 0)
  let target = random.range(0, totalWeight)
  for (const entry of allowed) {
    target -= entry.weight
    if (target <= 0) return entry
  }
  return allowed[allowed.length - 1] ?? ARCHETYPES[0]!
}

function createObstacles(archetype: ChunkArchetype, random: SeededRandom, length: number): ObstacleSlot[] {
  const result: ObstacleSlot[] = []
  const add = (z: number, x: number, width: number, behavior: ObstacleSlot['behavior'] = 'static') => {
    result.push({ id: `${archetype}-${result.length}`, z, x, width, depth: 2.2, height: 3.5, behavior, collider: behavior === 'rotate' ? 'obb' : 'box' })
  }
  if (archetype === 'slalom' || archetype === 'gauntlet') {
    for (let index = 0; index < 4; index += 1) add(18 + index * 13, index % 2 === 0 ? -4 : 4, 5)
  } else if (archetype === 'moving-gates') {
    add(length * 0.55, 0, 4.5, 'slide')
  } else if (archetype === 'rotating-hazards') {
    add(length * 0.58, 0, 8, 'rotate')
  } else if (archetype === 'crusher') {
    add(length * 0.55, 0, 7, 'crusher')
  } else if (archetype === 'precision-corridor') {
    add(length * 0.45, random.nextFloat() < 0.5 ? -3 : 3, 4)
  } else if (archetype === 'straight' && random.nextFloat() > 0.75) {
    add(length * 0.65, 0, 3)
  }
  return result
}

function safeCorridors(archetype: ChunkArchetype): SafeCorridor[] {
  if (archetype === 'split' || archetype === 'crusher') {
    return [{ x: -4.2, halfWidth: 2.5 }, { x: 4.2, halfWidth: 2.5 }]
  }
  if (archetype === 'precision-corridor') return [{ x: 0, halfWidth: 2.4 }]
  return [{ x: 0, halfWidth: 5.5 }]
}

export class ChunkGenerator {
  private readonly random: SeededRandom
  private connector: ConnectorProfile = 'standard'

  public constructor(seed: number) {
    this.random = new SeededRandom(seed)
  }

  public generate(index: number, startZ: number): TrackChunkDefinition {
    const random = this.random.fork(index)
    const tier = difficultyTier(startZ)
    const archetype = index === 0 ? ARCHETYPES[0]! : weightedChoice(random, tier)
    const length = Math.round(random.range(58, 88))
    const width = archetype.id === 'narrow' || archetype.id === 'precision-corridor'
      ? random.range(7.5, 10)
      : random.range(14, 20)
    const entryProfile = this.connector
    const exitProfile: ConnectorProfile = 'standard'
    this.connector = exitProfile
    const isGap = archetype.id === 'gap' || archetype.id === 'void-islands' || archetype.id === 'ramp'
    return {
      id: `${index}-${archetype.id}`,
      index,
      archetype: archetype.id,
      recipeId: `${archetype.id}-${index % 6}`,
      minimumTier: archetype.minimumTier,
      length,
      width,
      startZ,
      entryProfile,
      exitProfile,
      pitch: archetype.id === 'ramp' ? -0.12 : archetype.id === 'wave' ? random.range(-0.08, 0.08) : 0,
      bank: archetype.id === 'banking' ? random.range(-0.24, 0.24) : 0,
      elevation: 0,
      curve: archetype.id === 'banking' || archetype.id === 'slalom' || archetype.id === 'split'
        ? random.range(-4.5, 4.5)
        : 0,
      gapStart: isGap ? length * 0.48 : null,
      gapLength: isGap ? (archetype.id === 'ramp' ? 7 : Math.min(8.5, 4.5 + tier * 0.75)) : 0,
      obstacles: index === 0 ? [] : createObstacles(archetype.id, random, length),
      safeCorridors: safeCorridors(archetype.id),
      minimumReactionSeconds: tier >= 4 ? 0.75 : tier >= 3 ? 0.68 : 0.6,
    }
  }
}
